import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Calendar as CalendarIcon, Clock, Users, User, Mail, Phone, MessageSquare } from "lucide-react";
import { restaurantConfig } from "../config/restaurantConfig";
import Reveal from "../components/Reveal";
import SectionEyebrow from "../components/SectionEyebrow";

const FIELD_ORDER = ["date", "time", "guests", "name", "email", "phone", "notes"];

export default function Reservation() {
  const cfg = restaurantConfig.reservation;
  const f = cfg.fields;
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    date: "",
    time: cfg.timeSlots[0],
    guests: 2,
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.date)  e.date  = cfg.errors.date;
    if (!form.time)  e.time  = cfg.errors.time;
    if (!form.name.trim()) e.name = cfg.errors.name;
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = cfg.errors.email;
    if (!form.phone.trim()) e.phone = cfg.errors.phone;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSuccess(true);
  };

  const resetForm = () => {
    setSuccess(false);
    setForm({ date: "", time: cfg.timeSlots[0], guests: 2, name: "", email: "", phone: "", notes: "" });
  };

  return (
    <div data-testid="page-reservation" className="pb-24">
      <section className="pt-12 md:pt-20 pb-12 text-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <SectionEyebrow className="justify-center inline-flex">Réservation</SectionEyebrow>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl mt-6 italic" style={{ fontWeight: 400 }}>
              {cfg.title}
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-muted-foreground font-sans font-light">
              {cfg.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16">
        {/* Form */}
        <Reveal>
          <form
            data-testid="reservation-form"
            onSubmit={onSubmit}
            className="space-y-8 bg-card/40 border border-border/60 p-8 md:p-12"
          >
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.08 } },
              }}
              className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7"
            >
              {/* Date */}
              <Field icon={CalendarIcon} label={f.date.label} error={errors.date} index={FIELD_ORDER.indexOf("date")}>
                <input
                  type="date"
                  data-testid="reservation-date"
                  min={today}
                  value={form.date}
                  onChange={(e) => update("date", e.target.value)}
                  className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans"
                />
              </Field>

              {/* Time */}
              <Field icon={Clock} label={f.time.label} error={errors.time} index={FIELD_ORDER.indexOf("time")}>
                <select
                  data-testid="reservation-time"
                  value={form.time}
                  onChange={(e) => update("time", e.target.value)}
                  className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans"
                >
                  {cfg.timeSlots.map((t) => (
                    <option key={t} value={t} className="bg-background text-foreground">
                      {t}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Guests */}
              <Field icon={Users} label={f.guests.label} error={errors.guests} index={FIELD_ORDER.indexOf("guests")}>
                <select
                  data-testid="reservation-guests"
                  value={form.guests}
                  onChange={(e) => update("guests", Number(e.target.value))}
                  className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans"
                >
                  {Array.from({ length: cfg.maxGuests }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n} className="bg-background text-foreground">
                      {n} {n === 1 ? f.guests.unit : f.guests.unitPlural}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Name */}
              <Field icon={User} label={f.name.label} error={errors.name} index={FIELD_ORDER.indexOf("name")}>
                <input
                  type="text"
                  data-testid="reservation-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder={f.name.placeholder}
                  className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans placeholder:text-muted-foreground/60"
                />
              </Field>

              {/* Email */}
              <Field icon={Mail} label={f.email.label} error={errors.email} index={FIELD_ORDER.indexOf("email")}>
                <input
                  type="email"
                  data-testid="reservation-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder={f.email.placeholder}
                  className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans placeholder:text-muted-foreground/60"
                />
              </Field>

              {/* Phone */}
              <Field icon={Phone} label={f.phone.label} error={errors.phone} index={FIELD_ORDER.indexOf("phone")}>
                <input
                  type="tel"
                  data-testid="reservation-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder={f.phone.placeholder}
                  className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans placeholder:text-muted-foreground/60"
                />
              </Field>

              {/* Notes (full width) */}
              <div className="md:col-span-2">
                <Field icon={MessageSquare} label={f.notes.label} error={null} index={FIELD_ORDER.indexOf("notes")}>
                  <textarea
                    rows={3}
                    data-testid="reservation-notes"
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder={f.notes.placeholder}
                    className="w-full bg-transparent text-foreground border-b border-border/70 focus:border-primary outline-none py-2 font-sans resize-none placeholder:text-muted-foreground/60"
                  />
                </Field>
              </div>
            </motion.div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <p className="text-[11px] tracking-luxe-sm uppercase text-muted-foreground">
                {cfg.smallNote}
              </p>
              <button
                type="submit"
                data-testid="reservation-submit"
                className="px-8 py-4 bg-burgundy text-white text-xs tracking-luxe uppercase hover:bg-burgundy-hover transition-colors"
              >
                {cfg.submitLabel}
              </button>
            </div>
          </form>
        </Reveal>

        {/* Side */}
        <Reveal delay={0.15} className="lg:sticky lg:top-28 self-start">
          <div className="relative aspect-[4/5] overflow-hidden mb-8">
            <img
              src={restaurantConfig.images.atmosphere[1]}
              alt="Salle"
              className="w-full h-full object-cover img-cinematic"
            />
          </div>
          <h3 className="font-serif text-3xl italic" style={{ fontWeight: 400 }}>
            Un accueil <span className="text-accent-line">sur-mesure</span>.
          </h3>
          <p className="mt-4 text-muted-foreground font-sans font-light">
            Pour un anniversaire, un dîner d'affaires ou une privatisation,
            nos équipes vous répondent personnellement. N'hésitez pas à nous appeler.
          </p>
          <a
            href={restaurantConfig.contact.phoneLink}
            data-testid="reservation-phone-link"
            className="inline-flex items-center gap-3 mt-6 text-accent-line text-sm tracking-luxe-sm uppercase hover:gap-4 transition-all"
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} /> {restaurantConfig.contact.phoneDisplay}
          </a>
        </Reveal>
      </section>

      {/* Success modal */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="reservation-success-modal"
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 12, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 12, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-md w-full bg-background border border-primary/40 p-10 text-center"
            >
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 14 }}
                className="w-16 h-16 mx-auto rounded-full border-2 border-accent-line flex items-center justify-center text-accent-line"
              >
                <Check className="w-7 h-7" strokeWidth={2} />
              </motion.div>
              <h3 className="font-serif text-3xl mt-6 italic" style={{ fontWeight: 400 }}>
                {cfg.successTitle}
              </h3>
              <p className="mt-4 text-muted-foreground font-sans font-light leading-relaxed">
                {cfg.successMessage}
              </p>
              <div className="mt-8 text-left p-5 bg-card/60 border border-border/60 space-y-1.5 text-sm font-sans">
                <p><span className="text-muted-foreground">{cfg.summaryLabels.date}</span> <span className="text-foreground">{form.date}</span></p>
                <p><span className="text-muted-foreground">{cfg.summaryLabels.time}</span> <span className="text-foreground">{form.time}</span></p>
                <p><span className="text-muted-foreground">{cfg.summaryLabels.guests}</span> <span className="text-foreground">{form.guests}</span></p>
                <p><span className="text-muted-foreground">{cfg.summaryLabels.name}</span> <span className="text-foreground">{form.name}</span></p>
              </div>
              <button
                data-testid="reservation-success-close"
                onClick={resetForm}
                className="mt-8 px-8 py-4 border border-accent-line text-accent-line text-xs tracking-luxe uppercase hover:bg-burgundy dark:hover:bg-gold hover:text-white dark:hover:text-zinc-950 hover:border-transparent transition-colors"
              >
                {cfg.closeLabel}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function Field({ icon: Icon, label, error, children, index = 0 }) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 14 },
        show:   { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
      }}
    >
      <label className="flex items-center gap-2 text-[10px] tracking-luxe uppercase text-muted-foreground mb-2">
        <Icon className="w-3.5 h-3.5 text-accent-line" strokeWidth={1.5} />
        {label}
      </label>
      {children}
      {error && <p className="mt-1.5 text-[11px] text-destructive font-sans">{error}</p>}
    </motion.div>
  );
}
